// line1DirectionOfAttackTools.js — Dedicated LINE1 polyline collector (ticket 02).
//
// DrawRules.LINE1 JSDoc does NOT say tip=P1 — FLOT/LOC/wires share this rule with Direction
// of Attack. Discriminator (B): reverse only when SIDC entity maps via the library's own
// clsRenderer.getCMLineType → TacticalLines.DIRATK* (entity 140601/140602/140603). Not a
// hand-maintained SIDC allowlist; not getPath() text matching.

import { DrawRules, SymbolID, clsRenderer } from '@armyc2.c5isr.renderer/mil-sym-ts-web';
import Feature from 'ol/Feature';
import { LineString } from 'ol/geom';
import { registerDrawInteraction } from '../customDrawInteractions';
import { createSequentialCollector, reverseAll } from './sequentialCollector';
import { PREVIEW_LINE_BLUE } from './drawPreviewStyles';

// Seed DIRATK* line-type ints from the same entity codes getCMLineType maps — re-read on
// library upgrade by changing nothing if those entity codes still resolve to DIRATK*.
const _dirAtkSeedVersion = SymbolID.Version_2525Dch1;
const DIRATK_LINE_TYPES = new Set([
    clsRenderer.getCMLineType(_dirAtkSeedVersion, 140601), // DIRATKAIR
    clsRenderer.getCMLineType(_dirAtkSeedVersion, 140602), // DIRATKGND
    clsRenderer.getCMLineType(_dirAtkSeedVersion, 140603), // DIRATKSPT
]);

/** @param {number} lineType — result of clsRenderer.getCMLineType */
export function isDirAtkLineType(lineType) {
    return DIRATK_LINE_TYPES.has(lineType);
}

/**
 * True when this SIDC's entity maps to DIRATKAIR / DIRATKGND / DIRATKSPT in mil-sym-ts-web.
 * @param {string|null|undefined} sidc
 */
export function shouldReverseLine1Coords(sidc) {
    if (!sidc || typeof sidc !== 'string' || sidc.length < 20) return false;
    const version = SymbolID.getVersion(sidc);
    const entityCode = SymbolID.getEntityCode(sidc);
    return isDirAtkLineType(clsRenderer.getCMLineType(version, entityCode));
}

function makeLine1Collector(map, opts) {
    const sidc = opts.sidc || null;
    const reverse = shouldReverseLine1Coords(sidc);

    return createSequentialCollector(map, {
        minCount: 2,
        maxCount: Infinity,
        onFinish: opts.onFinish,
        reorderOnFinish: reverse ? reverseAll : (c) => c,
        sidc,
        previewMinPoints: 1,
        cursorFirst: reverse,
        applyReorderToPreview: reverse,
        roleForIndex: (i) => (i === 0 ? 'anchor' : 'direction'),
        styleResolver: () => PREVIEW_LINE_BLUE,
        onSketchUpdate: ({ previewSource, collected3857, cursorCoord3857 }) => {
            const pts = cursorCoord3857 ? [...collected3857, cursorCoord3857] : collected3857;
            if (pts.length >= 2) previewSource.addFeature(new Feature(new LineString(pts)));
        },
    });
}

registerDrawInteraction(DrawRules.LINE1, (map, opts) => makeLine1Collector(map, opts));
